import AppActivityIndicator from "@toothfairy/shared-ui/AppActivityIndicator";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import { showSideMenu } from "../../../../../App";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import execEnvsHelper from "../../../../API/ExecutionEnvironments/execEnvsHelper";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";

const ExecEnvsList = ({ isExecsLoading, setDisplayedView }) => {
  const { currentTheme } = AppTheme.useTheme();
  const { getActiveWorkspace } = useWorkspaces();
  const { width } = useWindowDimensions();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { user } = AppUser.useUser();
  const [showChoice, setShowChoice] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [selectedExecEnvId, setSelectedExecEnvId] = React.useState(null);
  const [displayedExecEnvs, setDisplayedexecEnvs] = React.useState([]);
  const [selectedExecEnvIds, setSelectedExecEnvIds] = React.useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = React.useState(false);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { height } = useWindowDimensions();
  const history = useHistory();
  const params = useParams();
  useEffect(() => {
    const optId = params?.optId;
    if (!optId) return;
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    if (isLoading) return;
    history.replace(`/workspaces/${wsId}/hooks/${optId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.optId, isLoading, getActiveWorkspace()?.id]);
  const columns = [
    {
      title: t("settings.list.id"),
      width: 0.00001,
    },
    {
      title: t("settings.list.name"),
      width: 0.25,
    },
    {
      title: t("settings.list.description"),
    },
    {
      title: t("settings.list.remoteEnvironment"),
      width: 0.15,
    },
    {
      title: t("settings.list.actions"),
      isAction: true,
      width: 0.1,
    },
  ];
  const loadExecEnvironments = useCallback(
    async (workspaceID) => {
      try {
        setIsLoading(true);
        const execEnvsData =
          await execEnvsHelper.getCustomCodeExecutionEnvironments(
            workspaceID,
            null
          );
        let execEnvs =
          execEnvsData?.data?.customCodeExecutionEnvironmentsByWorkspace?.items;
        execEnvs =
          execEnvs
            ?.map((u) => {
              if (u?._deleted !== true)
                return {
                  ...u,
                  customExecutionSecrets:
                    typeof u?.customExecutionSecrets === "string"
                      ? JSON.parse(u?.customExecutionSecrets)
                      : u?.customExecutionSecrets,
                  staticDocs:
                    typeof u?.staticDocs === "string"
                      ? JSON.parse(u?.staticDocs)
                      : u?.staticDocs,
                  staticArtifacts:
                    typeof u?.staticArtifacts === "string"
                      ? JSON.parse(u?.staticArtifacts)
                      : u?.staticArtifacts,
                };
            })
            ?.filter((u) => u != null) || [];

        dispatch("SET_EXEC_ENVS", execEnvs || []);

        setDisplayedexecEnvs(execEnvs);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch]
  );
  useEffect(() => {
    if (getActiveWorkspace()?.id)
      loadExecEnvironments(getActiveWorkspace()?.id);
  }, [getActiveWorkspace()?.id]);
  useEffect(() => {
    setDisplayedexecEnvs([...state?.execEnvs]);
  }, [state?.execEnvs]);

  function identifyOptByID(id) {
    return state.execEnvs.find((u) => u.id === id);
  }
  const handleExecEnvItemSelection = ({ item, index }) => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId || !item?.id) return;
    history.push(`/workspaces/${wsId}/hooks/${item.id}`);
  };
  const handleExecEnvItemDeletion = async (item, index) => {
    try {
      setShowChoice(false);
      const _id = selectedExecEnvId || selectedItem?.id;
      if (_id) {
        const functionsData =
          await execEnvsHelper.getCustomCodeExecutionEnvironments(
            state?.activeWorkspaceID
          );
        let functions =
          functionsData?.data?.CustomCodeExecutionEnvironmentsByWorkspace
            ?.items;
        functions =
          functions
            ?.map((u) => {
              if (u?.llmProvider === _id)
                return {
                  ...u,
                };
            })
            ?.filter((u) => u != null) || [];
        if (functions.length > 0) {
          AppToasty.show(t("execEnvDeletionErrorCaptionDueToConflict"), {
            type: "danger",
          });
          return;
        }
        const result =
          await execEnvsHelper.deleteCustomCodeExecutionEnvironment(_id);
        dispatch("SET_EXEC_ENVS", [
          ...state?.execEnvs.filter((u) => u.id !== _id),
        ]);
        AppToasty.show(t("execEnvDeletionSuccessCaption"), {
          type: "success",
        });
        setSelectedIndex(null);
        setSelectedExecEnvId(null);
        setSelectedItem(null);
      } else {
        throw new Error("No item selected");
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("execEnvsItemDeleteFailedCaption"), {
        type: "danger",
      });
    }
  };
  const handleDuplication = async (id) => {
    try {
      let item = state?.execEnvs?.find((u) => u.id === id);
      if (!item) {
        AppToasty.show(t("execEnvDuplicationErrorCaption"), {
          type: "danger",
        });
        return;
      }
      let newExecEnv = {
        ...item,
        name: `${item.name} - Copy`,
        workspaceID: getActiveWorkspace()?.id,
      };
      // Remove system fields
      delete newExecEnv.id;
      delete newExecEnv.createdAt;
      delete newExecEnv.updatedAt;
      delete newExecEnv._version;
      delete newExecEnv._deleted;
      delete newExecEnv._lastChangedAt;
      delete newExecEnv.__typename;
      // Stringify arrays if they exist
      if (newExecEnv?.customExecutionSecrets)
        newExecEnv.customExecutionSecrets = JSON.stringify(
          newExecEnv.customExecutionSecrets
        );
      if (newExecEnv?.staticDocs)
        newExecEnv.staticDocs = JSON.stringify(newExecEnv.staticDocs);
      if (newExecEnv?.staticArtifacts)
        newExecEnv.staticArtifacts = JSON.stringify(newExecEnv.staticArtifacts);

      const result = await execEnvsHelper.createCustomCodeExecutionEnvironment(
        newExecEnv,
        user?.id
      );
      if (result?.data?.createCustomCodeExecutionEnvironment?.id) {
        // Reload the execution environments list
        await loadExecEnvironments(getActiveWorkspace()?.id);
        AppToasty.show(t("execEnvDuplicationSuccessCaption"), {
          type: "success",
        });
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("execEnvDuplicationErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleBulkDeletion = async () => {
    try {
      for (const id of selectedExecEnvIds) {
        const functionsData =
          await execEnvsHelper.getCustomCodeExecutionEnvironments(
            state?.activeWorkspaceID
          );
        const inUse = (
          functionsData?.data?.CustomCodeExecutionEnvironmentsByWorkspace
            ?.items || []
        )
          .filter((u) => u != null)
          .some((u) => u?.llmProvider === id);
        if (inUse) {
          AppToasty.show(t("execEnvDeletionErrorCaptionDueToConflict"), {
            type: "danger",
          });
          continue;
        }
        await execEnvsHelper.deleteCustomCodeExecutionEnvironment(id);
      }
      dispatch("SET_EXEC_ENVS", [
        ...state?.execEnvs.filter((u) => !selectedExecEnvIds.includes(u.id)),
      ]);
      setSelectedExecEnvIds([]);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("execEnvDeletionSuccessCaption"), {
        type: "success",
      });
    } catch (error) {
      console.log(error);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("execEnvsItemDeleteFailedCaption"), {
        type: "danger",
      });
    }
  };
  const handleCreation = () => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    history.push(`/workspaces/${wsId}/hooks/new`);
  };
  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancel")}
        caption={t("settings.list.deleteExecEnvConfirmation")}
        subCaption={t("settings.list.deleteExecEnvConfirmationMessage")}
        confirmCaption={t("settings.list.confirmDelete")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handleExecEnvItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [setShowChoice, showChoice, selectedItem, setSelectedItem]);

  const _bulkDeleteChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancelConfirmation")}
        caption={t("settings.list.deleteSelectedConfirmation", {
          defaultValue: t("settings.list.deleteExecEnvConfirmation"),
        })}
        subCaption={t("settings.list.deleteExecEnvConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleBulkDeletion}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    );
  }, [showBulkDeleteChoice, selectedExecEnvIds]);

  return getActiveWorkspace()?.subscriptionType !== "base" ? (
    <View
      style={{
        paddingLeft: 10,
        flex: 1,
        width: showSideMenu() ? width - 220 : "100%",
        minWidth: "100%",
        overflow: isLoading && "hidden",
      }}
    >
      <>
        {isLoading ? (
          <ToothFairyLoadingPlaceholder
            backgroundColor={currentTheme?.theme?.softBackgroundColor}
            visible={true}
            containerStyle={{
              backgroundColor: currentTheme?.theme?.transparent,
              minHeight: 250,
            }}
            caption={t("settings.list.loadingExecEnvs")}
          />
        ) : (
          <>
            {displayedExecEnvs.length > 0 ? (
              <AppTable
                isSelectable
                showPagination
                itemsPerPage={50}
                hideSearch={false}
                searchPlaceholder={t("settings.toolbar.searchExecEnvs")}
                onSelectionChange={(selectedItems) => {
                  setSelectedExecEnvIds(selectedItems?.map((u) => u?.id) || []);
                }}
                bulkActions={[
                  {
                    id: "DELETE",
                    label: t("settings.list.delete"),
                    icon: AppAssets.icons.delete,
                    isDestructive: true,
                  },
                ]}
                onBulkActionPress={(id) => {
                  if (id === "DELETE" && selectedExecEnvIds?.length > 0)
                    setShowBulkDeleteChoice(true);
                }}
                actionButtons
                showDropDownMenuForActions
                mainActionLabel={t("settings.list.edit")}
                onClickMainAction={(item, index) => {
                  handleExecEnvItemSelection({ item, index });
                }}
                onActionItemPress={(id, item, index) => {
                  if (id === "EDIT") {
                    handleExecEnvItemSelection({ item, index });
                  } else if (id === "DUPLICATE") {
                    handleDuplication(item?.id);
                  } else if (id === "DELETE") {
                    setSelectedItem(item);
                    setSelectedIndex(index);
                    setSelectedExecEnvId(item?.id);
                    setShowChoice(true);
                  }
                }}
                actions={[
                  {
                    label: t("settings.list.edit"),
                    id: "EDIT",
                    icon: AppAssets.icons.edit,
                  },
                  {
                    label: t("settings.list.duplicate"),
                    id: "DUPLICATE",
                    icon: AppAssets.icons.duplicate,
                  },
                  {
                    label: t("settings.list.delete"),
                    id: "DELETE",
                    icon: AppAssets.icons.delete,
                  },
                ]}
                style={{
                  zIndex: -1,
                  marginTop: 15,
                  height:
                    displayedExecEnvs?.length >= 50 ? height - 95 : height - 75,
                  paddingRight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
                headers={columns}
                items={displayedExecEnvs
                  ?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
                  ?.map((u) => {
                    return {
                      id: u?.id || " ",
                      name: u?.name
                        ? u?.customCodeExecutionEnvironmentVersion
                          ? `${u.name} (v${u.customCodeExecutionEnvironmentVersion})`
                          : u.name
                        : " ",
                      description:
                        u?.description ||
                        t("settings.list.noDescriptionProvided"),
                      functionName:
                        u?.functionName ||
                        t("settings.list.defaultExecutionEnv"),
                      actions: " ",
                    };
                  })}
              >
                <ToothFairyButton
                  title={t("common.create")}
                  onPress={handleCreation}
                />
              </AppTable>
            ) : (
              <AppPlaceholderView
                wrapperStyle={{
                  width: "100%",
                  height: "100%",
                  marginVertical: 60,
                  marginHorizontal: "auto",
                  flex: 1,
                  zIndex: -1,
                }}
                size={30}
                icon={AppAssets.icons.terminal}
                mainCaption={t("settings.list.noExecEnvsFound")}
                subCaption={t("settings.list.noExecEnvsFoundSub")}
                actionLabel={t("common.create")}
                onActionPress={handleCreation}
              />
            )}
          </>
        )}
      </>

      {_coreChoice}
      {_bulkDeleteChoice}
    </View>
  ) : (
    <AppPlaceholderView
      accessibilityLabel="code-execution-environment-availability"
      wrapperStyle={{
        maxWidth: 400,
        height: 200,
        marginHorizontal: "auto",
        marginTop: 50,
      }}
      size={30}
      icon={AppAssets.icons.accessDenied}
      mainCaption={t("settings.list.executionHooksNotAvailable")}
      subCaption={t("settings.list.upgradeToEnterprise")}
    />
  );
};

export default ExecEnvsList;
